import {
  OrganizationService,
  OrganizationVM,
} from "@propertelligent/client-api";
import { useEffect, useMemo, useState } from "react";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import TextButton from "../../../newComponents/buttons/text-button";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import EmptyState from "../../../newComponents/emptystate";
import SimpleSpinner from "../../loader";
import { GetImages } from "../../../utils/GetImages";
import apiHelper from "../../../utils/apiHelper";
import DateHelper from "../../../utils/DateHelper";
import {
  MaintenanceBannerService,
  MaintenanceBannerVM,
} from "../../../services/maintenanceBanner.service";
import MaintenanceBannerDrawer from "./MaintenanceBannerDrawer";
import "../platform-portal.css";

const getBannerStatus = (banner: MaintenanceBannerVM) => {
  const now = new Date();
  const startTime = banner.startTimeUtc ? new Date(banner.startTimeUtc) : null;
  const endTime = banner.endTimeUtc ? new Date(banner.endTimeUtc) : null;

  if (endTime && endTime < now) {
    return { label: "Expired", className: "expired" };
  }

  if (startTime && startTime > now) {
    return { label: "Scheduled", className: "scheduled" };
  }

  return { label: "Active", className: "active" };
};

const MaintenanceBanners = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<MaintenanceBannerVM[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationVM[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<MaintenanceBannerVM | null>(null);
  const [deleteBanner, setDeleteBanner] = useState<MaintenanceBannerVM | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const organizationLookup = useMemo(
    () =>
      new Map(
        organizations.map((organization) => [
          Number(organization.id),
          organization.name || `Organization ${organization.id}`,
        ]),
      ),
    [organizations],
  );

  const fetchData = async () => {
    setLoading(true);

    const [bannersResponse, organizationsResponse] = await Promise.all([
      apiHelper(MaintenanceBannerService.getApiMaintenanceBanner, {
        showNotification: false,
      }),
      apiHelper(OrganizationService.getApiOrganization, {
        showNotification: false,
      }),
    ]);

    const bannerSuccess = bannersResponse?.data?.success;
    const organizationSuccess = organizationsResponse?.data?.success;

    setRows(Array.isArray(bannerSuccess) ? bannerSuccess : bannerSuccess ? [bannerSuccess] : []);
    setOrganizations(
      Array.isArray(organizationSuccess)
        ? organizationSuccess
        : organizationSuccess
          ? [organizationSuccess]
          : [],
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async () => {
    if (!deleteBanner?.id) {
      return;
    }

    setIsDeleting(true);
    const response = await apiHelper(
      MaintenanceBannerService.deleteApiMaintenanceBanner,
      {
        showNotification: true,
        successMessage: "Maintenance banner deleted",
        failureMessage: "Unable to delete maintenance banner",
      },
      Number(deleteBanner.id),
    );

    setIsDeleting(false);

    if (response?.data?.success) {
      setDeleteBanner(null);
      fetchData();
    }
  };

  const getAppliesToLabel = (banner: MaintenanceBannerVM) => {
    if (banner.entityId == null) {
      return "All Users";
    }

    return organizationLookup.get(Number(banner.entityId)) || `Organization ${banner.entityId}`;
  };

  return (
    <div className="platform-portal-page">
      <div className="top-listing-header-top d-flex justify-content-between align-items-center gap-2 flex-wrap">
        <h2 className="H2">Maintenance Banners</h2>
        <SimpleButton
          label="Add Banner"
          onClick={() => {
            setEditingBanner(null);
            setIsDrawerOpen(true);
          }}
        />
      </div>

      {loading ? (
        <div className="platform-loadmore">
          <SimpleSpinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={GetImages.emptyStateData}
          heading="No maintenance banners"
          isButton={false}
          isBorder={false}
        />
      ) : (
        <div className="platform-table-wrapper">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Start</th>
                <th>End</th>
                <th>Applies To</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const status = getBannerStatus(row);

                return (
                  <tr key={row.id}>
                    <td>{row.title || "—"}</td>
                    <td>
                      {row.startTimeUtc
                        ? DateHelper.convertUTCtoDateTimeSimple(row.startTimeUtc, true)
                        : "—"}
                    </td>
                    <td>
                      {row.endTimeUtc
                        ? DateHelper.convertUTCtoDateTimeSimple(row.endTimeUtc, true)
                        : "—"}
                    </td>
                    <td>{getAppliesToLabel(row)}</td>
                    <td>
                      <span
                        className={`platform-maintenance-badge platform-maintenance-badge-${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <div className="platform-maintenance-actions">
                        <TextButton
                          label="Edit"
                          onClick={() => {
                            setEditingBanner(row);
                            setIsDrawerOpen(true);
                          }}
                        />
                        <TextButton
                          label="Delete"
                          onClick={() => setDeleteBanner(row)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <MaintenanceBannerDrawer
        isOpen={isDrawerOpen}
        banner={editingBanner}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditingBanner(null);
        }}
        onSaved={fetchData}
      />

      <ConfirmationPopup
        isOpen={Boolean(deleteBanner)}
        title="Delete Maintenance Banner"
        message={`Are you sure you want to delete "${deleteBanner?.title || "this maintenance banner"}"?`}
        primaryButtonLabel={isDeleting ? "Deleting..." : "Delete"}
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDelete}
        onSecondaryAction={() => setDeleteBanner(null)}
        onClose={() => setDeleteBanner(null)}
        isPrimaryDestructive={true}
        isLoading={isDeleting}
        loadingLabel="Deleting..."
      />
    </div>
  );
};

export default MaintenanceBanners;
